// code-guardian-ignore-duplicates: parallel master-page shell (tab kept in ?tab=, selectTab, link-search clean-up, action slot) mirrored from StoreMasterPage on purpose so every tabbed master page behaves alike
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SlidersHorizontal } from 'lucide-react';
import EmployeesPage from './EmployeesPage';
import EmployeeCustomFieldsPage from './EmployeeCustomFieldsPage';
import MasterTabStrip, { type MasterTab } from '../components/MasterTabStrip';
import { rosterTabs, type RosterCounts, type RosterTab } from '../utils/employeeRosterTabs';

// The address values a link can use to open this page on one row.
const LINK_KEYS = ['employeeCode', 'storeCode', 'q'] as const;

/**
 * Master Data → Employees.
 *
 * Mirrors StoreMasterPage: the employee roster and its per-project custom-field
 * master are two halves of one page. Both are the existing pages in `embedded` mode —
 * neither is rewritten. Each keeps its own data fetching and modals; this shell supplies
 * the heading and the one row under it: the roster's three lists (Deployed, Undeployed,
 * Inactive) and Custom Fields as four tabs on the left, the open tab's own actions
 * (Bulk Upload and Download on the roster) portalled in on the right.
 */

// The two halves, as the address names them (?tab=employees / ?tab=custom-fields).
type Tab = 'employees' | 'custom-fields';

const TABS: { key: Tab; blurb: string }[] = [
  {
    key: 'employees',
    blurb: 'The people on this project — their role, reporting line, and the store each is deployed to.',
  },
  {
    key: 'custom-fields',
    blurb:
      'Extra per-employee details this project wants, defined by you — the field name, its input type, and the exact values users may pick. They appear on the employee modal, in the bulk upload template, and (when flagged) on the mobile home screen.',
  },
];

// One strip, four tabs: the roster's three lists, then Custom Fields.
type StripTab = RosterTab | 'custom-fields';

const CUSTOM_FIELDS_TAB: MasterTab<StripTab> = { key: 'custom-fields', label: 'Custom Fields', icon: SlidersHorizontal };

export default function EmployeeMasterPage() {
  // The half lives in the URL so a link can point straight at either one. The retired
  // /master-management/employee-custom-fields bookmark lands here on ?tab=custom-fields.
  const [searchParams, setSearchParams] = useSearchParams();
  const fromUrl = searchParams.get('tab');
  const [activeTab, setActiveTab] = useState<Tab>(
    TABS.some((t) => t.key === fromUrl) ? (fromUrl as Tab) : 'employees',
  );
  // Which of the roster's lists is open. Not in the address, as it never was: the
  // roster opens on Deployed, and a link that names a person moves it to the list that
  // person is on (EmployeesPage decides that, and tells this page).
  const [rosterTab, setRosterTab] = useState<RosterTab>('deployed');
  // The roster's counts, reported up by EmployeesPage while it is open. Custom Fields
  // does not load the roster, so its tabs show their names only there.
  const [rosterCounts, setRosterCounts] = useState<RosterCounts | null>(null);

  // A link can open the Employees tab on one person: ?q=<their employee code>, as on
  // StoreMasterPage. The tab starts its search box with it, once, as the page opens.
  // Search's links name the person by code (employeeCode / storeCode), the drill-down
  // links by free text (q): whichever is there starts the box.
  const searchFromLink = (LINK_KEYS.map((k) => searchParams.get(k)).find((v) => v && v.trim()) ?? '').trim();
  // Then q leaves the address (replaced, so Back does not bring it back). Left there it
  // would follow the person to Custom Fields, because selectTab copies every address
  // value forward, and it would go on naming a search the person has since changed.
  useEffect(() => {
    if (!LINK_KEYS.some((k) => searchParams.has(k))) return;
    const next = new URLSearchParams(searchParams);
    for (const k of LINK_KEYS) next.delete(k);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  // The tab strip and the open tab's primary actions share one row; each half
  // portals its own actions into this node (callback ref so children re-render once
  // it exists).
  const [actionSlot, setActionSlot] = useState<HTMLDivElement | null>(null);

  // The roster size, reported up by the embedded EmployeesPage, shown next to the
  // heading on the Employees tab.
  const [employeeCount, setEmployeeCount] = useState<number | null>(null);

  const selectTab = (tab: Tab) => {
    setActiveTab(tab);
    const next = new URLSearchParams(searchParams);
    next.set('tab', tab);
    setSearchParams(next, { replace: true });
  };

  // A roster list opens the roster on that list. From the roster it is a change of list
  // only, which the address never carried; from Custom Fields it is the move back to the
  // roster, written as ?tab=employees as the old Employees tab did.
  const selectStripTab = (tab: StripTab) => {
    if (tab === 'custom-fields') {
      if (activeTab === 'custom-fields') return;
      setRosterCounts(null);
      selectTab('custom-fields');
      return;
    }
    setRosterTab(tab);
    if (activeTab !== 'employees') selectTab('employees');
  };

  const current = TABS.find((t) => t.key === activeTab) ?? TABS[0];
  const stripTabs: MasterTab<StripTab>[] = [
    ...rosterTabs(activeTab === 'employees' ? rosterCounts : null),
    CUSTOM_FIELDS_TAB,
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          Employees
          {activeTab === 'employees' && employeeCount !== null && (
            <span className="ml-2 text-base font-medium text-gray-400">({employeeCount.toLocaleString()})</span>
          )}
        </h1>
        <p className="mt-1 text-sm text-gray-500">{current.blurb}</p>
      </div>

      {/* Tabs left, the open tab's actions right — one row, no empty band. */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <MasterTabStrip
          tabs={stripTabs}
          active={activeTab === 'custom-fields' ? 'custom-fields' : rosterTab}
          onSelect={selectStripTab}
        />
        <div ref={setActionSlot} className="flex flex-wrap items-center gap-2" />
      </div>

      {/* Mounted on demand: each half fetches on mount. */}
      {activeTab === 'employees' && (
        <EmployeesPage
          embedded
          actionSlot={actionSlot}
          onCount={setEmployeeCount}
          initialSearch={searchFromLink}
          rosterTab={rosterTab}
          onRosterTabChange={setRosterTab}
          onTabCounts={setRosterCounts}
        />
      )}
      {activeTab === 'custom-fields' && (
        <EmployeeCustomFieldsPage embedded actionSlot={actionSlot} />
      )}
    </div>
  );
}
